import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Loader2, TrendingUp, Users, XCircle, RefreshCw, Save } from 'lucide-react';

/**
 * Super-admin dashboard for the free-demo funnel.
 * - Reads `/api/admin/demo/stats` for funnel + recent activity
 * - Reads/writes `/api/admin/demo/config` to tune the global daily cap
 *   without a redeploy (persists to site_settings.demo_config).
 * Rendered inside AdminPanel as its own tab.
 */
export default function AdminDemoFunnelTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [capDraft, setCapDraft] = useState('');
  const [ipHoursDraft, setIpHoursDraft] = useState('');
  const [expiryDraft, setExpiryDraft] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiFetch('/admin/demo/stats');
      setStats(data);
      setCapDraft(String(data.config.global_daily_cap));
      setIpHoursDraft(String(data.config.per_ip_hours));
      setExpiryDraft(String(data.config.expiry_hours));
    } catch (err) {
      toast.error(err?.message || 'Failed to load demo stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSaveConfig = async () => {
    if (saving) return;
    const cap = parseInt(capDraft, 10);
    const iph = parseInt(ipHoursDraft, 10);
    const exp = parseInt(expiryDraft, 10);
    if (Number.isNaN(cap) || cap < 0 || cap > 100000) {
      toast.error('Global daily cap must be between 0 and 100 000');
      return;
    }
    if (Number.isNaN(iph) || iph < 1 || iph > 720) {
      toast.error('Per-IP hours must be between 1 and 720');
      return;
    }
    if (Number.isNaN(exp) || exp < 1 || exp > 720) {
      toast.error('Expiry hours must be between 1 and 720');
      return;
    }
    setSaving(true);
    try {
      await apiFetch('/admin/demo/config', {
        method: 'PATCH',
        body: JSON.stringify({
          global_daily_cap: cap,
          per_ip_hours: iph,
          expiry_hours: exp,
        }),
      });
      toast.success('Demo config saved');
      await load();
    } catch (err) {
      toast.error(err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !stats) {
    return (
      <div className="text-center py-16">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-white/50" />
      </div>
    );
  }
  if (!stats) return null;

  const { totals, today, last_7d, config, global_used_today, global_remaining_today, recent_pending, recent_claimed } = stats;

  return (
    <div className="space-y-6" data-testid="admin-demo-funnel-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Demo Funnel</h2>
          <p className="text-sm text-white/60">Try-before-signup activity, conversions, and runtime tuning.</p>
        </div>
        <Button
          variant="outline"
          onClick={load}
          disabled={loading}
          data-testid="demo-funnel-refresh-btn"
          className="border-white/20 text-white hover:bg-white/10"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* HEADLINE KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard
          testid="kpi-created"
          icon={<Users className="h-5 w-5" />}
          label="Total demos created"
          value={totals.created}
          sub={`today ${today.pending + today.claimed} · 7d ${last_7d.pending + last_7d.claimed}`}
        />
        <KpiCard
          testid="kpi-claimed"
          icon={<TrendingUp className="h-5 w-5 text-emerald-400" />}
          label="Claimed (signed up)"
          value={totals.claimed}
          sub={`today ${today.claimed} · 7d ${last_7d.claimed}`}
        />
        <KpiCard
          testid="kpi-conversion"
          icon={<TrendingUp className="h-5 w-5 text-purple-400" />}
          label="Conversion rate"
          value={`${totals.conversion_pct}%`}
          sub="claimed / total created"
        />
        <KpiCard
          testid="kpi-abandoned"
          icon={<XCircle className="h-5 w-5 text-amber-400" />}
          label="Pending / abandoning"
          value={totals.pending}
          sub={`auto-purged after ${config.expiry_hours}h`}
        />
      </div>

      {/* QUOTA GAUGE */}
      <div className="glass-card p-5" data-testid="demo-quota-gauge">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-medium text-white">Today&apos;s global cap</div>
          <div className="text-sm text-white/60">
            {global_used_today} / {config.global_daily_cap} used · {global_remaining_today} remaining
          </div>
        </div>
        <div className="w-full h-2 rounded-full bg-white/10 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-purple-500 to-emerald-400 transition-all"
            style={{ width: `${Math.min(100, (global_used_today / Math.max(1, config.global_daily_cap)) * 100)}%` }}
          />
        </div>
      </div>

      {/* RUNTIME CONFIG EDITOR */}
      <div className="glass-card p-6" data-testid="demo-config-editor">
        <h3 className="text-lg font-bold text-white mb-4">Runtime configuration</h3>
        <div className="grid md:grid-cols-3 gap-4 mb-4">
          <div>
            <Label className="text-white/70 text-xs mb-1 block">Global daily cap</Label>
            <Input
              type="number"
              min="0"
              max="100000"
              value={capDraft}
              onChange={(e) => setCapDraft(e.target.value)}
              className="bg-white/10 border-white/20 text-white"
              data-testid="demo-cap-input"
              disabled={saving}
            />
            <p className="text-xs text-white/40 mt-1">Max free demos site-wide per 24h</p>
          </div>
          <div>
            <Label className="text-white/70 text-xs mb-1 block">Per-IP rate window (hours)</Label>
            <Input
              type="number"
              min="1"
              max="720"
              value={ipHoursDraft}
              onChange={(e) => setIpHoursDraft(e.target.value)}
              className="bg-white/10 border-white/20 text-white"
              data-testid="demo-ip-hours-input"
              disabled={saving}
            />
            <p className="text-xs text-white/40 mt-1">One demo per IP per this many hours</p>
          </div>
          <div>
            <Label className="text-white/70 text-xs mb-1 block">Demo expiry (hours)</Label>
            <Input
              type="number"
              min="1"
              max="720"
              value={expiryDraft}
              onChange={(e) => setExpiryDraft(e.target.value)}
              className="bg-white/10 border-white/20 text-white"
              data-testid="demo-expiry-input"
              disabled={saving}
            />
            <p className="text-xs text-white/40 mt-1">Unclaimed demos are auto-purged after this</p>
          </div>
        </div>
        <Button
          onClick={handleSaveConfig}
          disabled={saving}
          className="bg-purple-600 hover:bg-purple-500 text-white"
          data-testid="demo-config-save-btn"
        >
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save changes
        </Button>
      </div>

      {/* RECENT ACTIVITY FEEDS */}
      <div className="grid md:grid-cols-2 gap-4">
        <ActivityCard
          testid="pending-feed"
          title={`Recent pending (${recent_pending.length})`}
          empty="No pending demos right now."
          rows={recent_pending.map((d) => ({
            key: d.demo_id,
            primary: d.demo_id.slice(0, 20) + '…',
            secondary: `IP ${d.ip_address || '—'}`,
            timestamp: d.created_at,
          }))}
        />
        <ActivityCard
          testid="claimed-feed"
          title={`Recently claimed (${recent_claimed.length})`}
          empty="No claimed demos yet."
          rows={recent_claimed.map((s) => ({
            key: s.song_id,
            primary: s.title || 'Untitled',
            secondary: `user ${s.user_id?.slice(0, 20) || '—'}`,
            timestamp: s.unlocked_at || s.created_at,
          }))}
        />
      </div>
    </div>
  );
}

function KpiCard({ icon, label, value, sub, testid }) {
  return (
    <div className="glass-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-white/60 text-xs mb-2">
        {icon}<span>{label}</span>
      </div>
      <div className="text-2xl font-bold text-white">{value}</div>
      <div className="text-xs text-white/40 mt-1">{sub}</div>
    </div>
  );
}

function ActivityCard({ title, rows, empty, testid }) {
  return (
    <div className="glass-card p-5" data-testid={testid}>
      <h4 className="text-sm font-medium text-white mb-3">{title}</h4>
      {rows.length === 0 ? (
        <p className="text-xs text-white/40 italic">{empty}</p>
      ) : (
        <ul className="space-y-2 max-h-[320px] overflow-y-auto">
          {rows.map((r) => (
            <li key={r.key} className="text-xs border border-white/10 rounded-md p-2.5 bg-white/5">
              <div className="text-white font-medium truncate">{r.primary}</div>
              <div className="flex justify-between text-white/50 mt-0.5">
                <span className="truncate">{r.secondary}</span>
                <span className="whitespace-nowrap ml-2">{formatTime(r.timestamp)}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function formatTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}
